/** @jsx jsx */
import { jsx } from '@emotion/core';
import React from 'react';
import ReactDOMServer from 'react-dom/server';
import PropTypes from 'prop-types';
import get from 'lodash/get';
import { withNamespaces } from 'react-i18next';
import Swiper from 'react-id-swiper';
import Lottie from 'react-lottie';
import loadingAnimation from '../../../../../assets/animations/gray-load.json';
import Picture from '../../../../ui/Picture';
import { Icon } from '../../../../ui/Icon';
import { CLOUDINARY_BASE_VALUES } from '../../../../../utils/constants';
import { SafePureComponent } from '../../../../../utils/SafePureComponent';
import { getSlideSrc } from '../index.js';
import 'react-id-swiper/src/styles/css/swiper.css';
import { Breakpoints } from '../../../../../styles/Grid/GridSizes';
import {
  swiperCss,
  nextButtonCss,
  prevButtonCss,
  pictureCss,
  imageCss,
  regusPaginationActiveNumberCss,
  regusPaginationDashCss,
  regusPaginationNumberCss,
} from './index.styles';

const animationOptions = {
  loop: true,
  autoplay: true,
  animationData: loadingAnimation,
};

const baseImageConfigs = {
  [Breakpoints.xs]: {
    ...CLOUDINARY_BASE_VALUES,
  },
  [Breakpoints.sm]: {
    ...CLOUDINARY_BASE_VALUES,
  },
  [Breakpoints.md]: {
    ...CLOUDINARY_BASE_VALUES,
  },
  [Breakpoints.lg]: {
    ...CLOUDINARY_BASE_VALUES,
  },
  [Breakpoints.xl]: {
    ...CLOUDINARY_BASE_VALUES,
  },
};

const getPaginationElement = (currentClass, totalClass) =>
  ReactDOMServer.renderToStaticMarkup(
    <React.Fragment>
      <span css={regusPaginationActiveNumberCss} className={currentClass} />
      <span css={regusPaginationDashCss} />
      <span css={regusPaginationNumberCss} className={totalClass} />
    </React.Fragment>,
  );

const getPaginationFraction = number => (number < 10 ? `0${number}` : number);

class LocationSlider extends SafePureComponent {
  swiper = node => (this.swiper = node && node.swiper);
  state = {
    swiperParams: {
      initialSlide: get(this.props, 'slideToOpen', 0),
      navigation: {
        nextEl: '.regus-button-next',
        prevEl: '.regus-button-prev',
      },
      preloadImages: false,
      lazy: {
        loadPrevNext: true,
        loadPrevNextAmount: 1,
        loadOnTransitionStart: false,
      },
      loop: true,
      freeMode: true,
      pagination: {
        el: '.swiper-pagination',
        renderFraction: (currentClass, totalClass) =>
          getPaginationElement(currentClass, totalClass),
        formatFractionCurrent: number => getPaginationFraction(number),
        formatFractionTotal: number => getPaginationFraction(number),
        type: 'fraction',
      },
      keyboard: {
        enabled: true,
      },
      mousewheel: true,
      centeredSlides: true,
      spaceBetween: 120,
      observer: true,
      shouldSwiperUpdate: true,
      rebuildOnUpdate: true,
      slidesPerView: 2,
    },
  };

  componentDidMount() {
    const GRABBED = 'grabbed';

    if (this.swiper) {
      this.swiper.on('touchStart', () => {
        this.swiper.wrapperEl.classList.add(GRABBED);
      });

      this.swiper.on('touchEnd', () => {
        this.swiper.wrapperEl.classList.remove(GRABBED);
      });
    }
  }

  renderImages() {
    const { fields, t, slides } = this.props;
    const locationID = get(fields, 'ProtonId.value');
    const city = get(fields, 'City.value');

    return slides.map((slide, index) => {
      const imageSrc = {
        value: { src: getSlideSrc(locationID, slide.number) },
      };
      const imageAlt = `${t(slide.keyValue)} ${city}`;

      return (
        <div key={index}>
          <Picture
            image={imageSrc}
            fallbackAlt={imageAlt}
            imageSizes={baseImageConfigs}
            pictureCss={pictureCss}
            imageCss={imageCss}
            isClientLink
            instantLoad
            pictureClassName="swiper-lazy"
          />
          <div className="swiper-lazy-preloader">
            <Lottie
              options={animationOptions}
              isClickToPauseDisabled
              isStopped={false}
              height={80}
            />
          </div>
        </div>
      );
    });
  }

  goPrev = () => {
    this.swiper && this.swiper.slidePrev();
  };

  goNext = () => {
    this.swiper && this.swiper.slideNext();
  };

  safeRender() {
    return (
      <div css={swiperCss}>
        <Swiper ref={this.swiper} {...this.state.swiperParams}>
          {this.renderImages()}
        </Swiper>

        <button css={prevButtonCss} onClick={this.goPrev}>
          <Icon name="arrow-left" />
        </button>

        <button css={nextButtonCss} onClick={this.goNext}>
          <Icon name="arrow-right" />
        </button>
      </div>
    );
  }
}

LocationSlider.displayName = 'LocationHeader/ImageGallery/LocationSlider';

LocationSlider.defaultProps = { fields: {}, t: () => {} };

LocationSlider.propTypes = {
  fields: PropTypes.object,
  t: PropTypes.func,
};

export default withNamespaces()(LocationSlider);
